import { useMutation, useQueryClient } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import type { EntityKind } from "../Dashboard/types";
import {
  RMS_BOOTSTRAP_QUERY_KEY,
  useRmsBootstrap,
  type InteractionOpens,
  type PaletteColor,
} from "./bootstrap";
import { getEntityKindConfig } from "../Dashboard/defaultDashboardConfigs";

// ── Types ────────────────────────────────────────────────────────────────────

export type DashboardPanelType =
  | "facts"
  | "interactions"
  | "documents"
  | "holdings"
  | "performance"
  | "relationships"
  | "contacts"
  | "activity";

export interface PanelFilterDefaults {
  participant_only?: boolean;
  // show_passing_mentions (#10406), show_investment_vehicles (#11149) and
  // include_interaction_attachments (#11593, retired by #12023) are gone;
  // stale keys may remain in stored org config blobs and are simply no
  // longer read. The interaction-attachment arm itself is gone too (#13007):
  // an attachment reaches a record only through its own links.
}

// What one controller value says about the rest of the record (#10968).
// `hide` and the keys of `labels` are **stored attribute names**; the keys of
// the enclosing `rules` map are **stored enum values**. All three are matched
// exactly by the server, and a typo matches nothing silently — which is why the
// settings UI sources every one of them from the live schema rather than a text
// input. Only a label's value is free text.
export interface ConditionalFactsRule {
  hide?: string[];
  labels?: Record<string, string>;
}

export interface ConditionalFactsSettings {
  controller: string;
  rules: Record<string, ConditionalFactsRule>;
}

export interface EntityKindDashboardConfig {
  // The old panel layout (#13370 round 17): read by nothing, still in stored
  // configs, no longer written (the PUT drops them) or defaulted.
  mode?: "3-position" | "4-position";
  left?: DashboardPanelType;
  middle?: DashboardPanelType;
  middle_top?: DashboardPanelType;
  middle_bottom?: DashboardPanelType;
  right?: DashboardPanelType;
  filter_defaults?: Partial<Record<DashboardPanelType, PanelFilterDefaults>>;
  facts_pinned_fields?: string[];
  // Fact names hidden from the dashboard Facts panel. They remain fully
  // visible/editable on the Facts setup tab — this only declutters the
  // dashboard (e.g. IDs and reference data the user tracks but rarely reads).
  facts_hidden_fields?: string[];
  // Interaction custom-attribute field names hidden from the interaction
  // editor (#10243 / VU req #5). Mirrors `facts_hidden_fields`: the columns
  // still exist and are AI-fillable, this just declutters the editor for orgs
  // that don't track them. Only meaningful under the `interaction` key.
  interaction_hidden_fields?: string[];
  // Columns shown in the full Documents tab (variant="full").
  documents_visible_columns?: string[];
  // Columns shown in the smaller documents panel preview on the entity
  // overview (variant="compact"). Lets an org show many columns in the big
  // tab but a reduced set in the cramped panel. Falls back to
  // `documents_visible_columns` when unset (backward compatible).
  documents_panel_visible_columns?: string[];
  // Per-record fact visibility / labelling driven by a controller fact (#10968).
  // Absent → the feature is off for this record type.
  conditional_facts?: ConditionalFactsSettings;
  // The fact the identity strip reads for the record's status (#11426).
  // Absent → the `Status` convention the vehicle dashboard already follows.
  status_field?: string;
  // Maps this org's own status vocabulary onto the five semantic states, so a
  // client keeps its words ("Committed", "Wound down") and the colour comes
  // from the meaning rather than from a per-value hue (#11426). Keys are
  // STORED enum values, matched exactly. A value absent from the map renders
  // in the `none` state: no mapping means we were not told what the word
  // means, and guessing a judgement is worse than declining to show one.
  status_semantics?: Record<string, string>;
  // Ordered attributes the Documents list groups by (#11141): the first
  // buckets the list, the second subgroups within each bucket. Absent → the
  // document group alone, which is what the list always did. Stored as a list
  // so a third level is a rendering change rather than a config migration;
  // only the first two are read today.
  documents_group_by?: string[];
}

export const DEFAULT_DOCUMENTS_VISIBLE_COLUMNS: string[] = [
  "filename",
  "document_date",
];

// Resolve the visible document columns for a given Documents view variant.
// The page layout's List columns lead (#13370, round 17: the Documents
// settings moved from the Dashboard defaults to the layout); the stored
// `documents_visible_columns` stands in where the layout sets none. The
// compact overview panel prefers its own reduced column set, falling back to
// the full tab set, then the global default.
export const pickDocumentsVisibleColumns = (
  entityConfig: EntityKindDashboardConfig | undefined,
  variant: "compact" | "full",
  layoutColumns: readonly string[] | null = null
): string[] => {
  const tabColumns =
    (layoutColumns ? [...layoutColumns] : null) ??
    entityConfig?.documents_visible_columns ??
    DEFAULT_DOCUMENTS_VISIBLE_COLUMNS;
  if (variant === "compact") {
    return entityConfig?.documents_panel_visible_columns ?? tabColumns;
  }
  return tabColumns;
};

// The attribute the Documents list groups by when the org has configured
// nothing. Kept as an explicit constant rather than an empty string so the
// settings UI has something to select, but deliberately NOT sent on the wire:
// an unconfigured org must keep making the exact request it made before the
// setting existed.
export const DEFAULT_DOCUMENTS_GROUP_BY = "document_group";

export interface DocumentsGrouping {
  // Undefined when the org configured nothing — the caller omits the
  // parameter entirely rather than sending the default explicitly.
  groupBy?: string;
  subgroupBy?: string;
}

// Resolve the org's grouping choice for a record type's Documents list: the
// page layout's `list_group_by` first (#13370, round 17), else the stored
// `documents_group_by`. A list of one is a group with no subgrouping;
// anything past the second entry is ignored until the accordion renders a
// third level.
export const pickDocumentsGrouping = (
  entityConfig: EntityKindDashboardConfig | undefined,
  layoutGroupBy: readonly string[] | null = null
): DocumentsGrouping => {
  const configured = layoutGroupBy ?? entityConfig?.documents_group_by ?? [];
  return { groupBy: configured[0], subgroupBy: configured[1] };
};

// Global default for the interactions "Participant only" filter, used
// when an org hasn't set filter_defaults.interactions.participant_only
// in its dashboard config. On by default so entities aren't buried under
// every interaction they're merely mentioned in (#9853); orgs that
// explicitly turn it off still win via `?? DEFAULT_PARTICIPANT_ONLY`.
export const DEFAULT_PARTICIPANT_ONLY = true;

export type RmsDashboardConfig = Partial<
  Record<EntityKind, EntityKindDashboardConfig>
>;

/**
 * The config with its organization-wide settings beside the record types' (#13752, P3): the
 * palette, up to twelve named `#rrggbb`. Kept apart from `RmsDashboardConfig`, which readers
 * index by record type.
 */
export type OrganizationDashboardConfig = RmsDashboardConfig & {
  palette?: PaletteColor[];
  /** Where an interaction opens (#14870), Appearance's choice. */
  interaction_opens?: InteractionOpens;
  /** The holdings number columns whose total is on (#14991), Appearance's choice. */
  holdings_totalled_columns?: number[];
};

/** The "Participant only" default a record type's resolved config carries. */
export const kindParticipantOnly = (
  kindConfig: EntityKindDashboardConfig
): boolean =>
  kindConfig.filter_defaults?.interactions?.participant_only ??
  DEFAULT_PARTICIPANT_ONLY;

/**
 * The org's "Participant only" interactions default for a record type: what
 * the Interactions tab starts from and what the overview card lists (#12854).
 */
export const participantOnlyDefault = (
  entityKind: string,
  dashboardConfig: RmsDashboardConfig | undefined
): boolean =>
  kindParticipantOnly(
    getEntityKindConfig(entityKind as EntityKind, dashboardConfig)
  );

// ── API ──────────────────────────────────────────────────────────────────────

const configUrl = `${BASE_API_URL_V2}/api/v2/rms/dashboard-config`;

const saveDashboardConfig = async (
  config: RmsDashboardConfig
): Promise<RmsDashboardConfig> => {
  const response = await fpFetch(configUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ config }),
  });
  if (response.status === 400) {
    // A refusal names what is wrong (a colour of the palette, #13752 P3.1).
    const body: unknown = await response.json().catch(() => null);
    const detail =
      body && typeof body === "object" && "detail" in body
        ? (body as { detail: unknown }).detail
        : null;
    if (typeof detail === "string" && detail)
      throw new DashboardConfigRefused(detail);
  }
  if (!response.ok) {
    throw new Error(`Failed to update dashboard config: ${response.status}`);
  }
  const data = await response.json();
  return data.config;
};

/** A save the server refused, with its reason in words the settings page can show. */
export class DashboardConfigRefused extends Error {}

// ── Hooks ────────────────────────────────────────────────────────────────────

export const useDashboardConfig = (options?: { enabled?: boolean }) => {
  return useRmsBootstrap<RmsDashboardConfig>({
    select: (data) => data.dashboard_config,
    enabled: options?.enabled,
  });
};

export interface EditableDashboardConfig {
  config: RmsDashboardConfig | undefined;
  isLoading: boolean;
  // True when the read finished without producing a config at all.
  //
  // Saving writes back a WHOLE entity-kind object (the endpoint shallow-merges
  // at the top level only), and every editor falls back to
  // DEFAULT_DASHBOARD_CONFIGS when it has no stored config to start from. So a
  // tab that renders after a failed GET would replace the org's real settings
  // with defaults on its first save -- silently dropping
  // documents_visible_columns, facts_pinned_fields, facts_hidden_fields and
  // conditional_facts. Any tab that writes this config must refuse to do so
  // while this is true.
  //
  // An org that has simply never configured anything is NOT a failure: the
  // endpoint returns `{}`, which is a perfectly good starting point. Only an
  // absent config counts, which is why this tests for `undefined` rather than
  // falsiness.
  failedToLoad: boolean;
}

export const useEditableDashboardConfig = (): EditableDashboardConfig => {
  const { data, isLoading } = useDashboardConfig();
  return {
    config: data,
    isLoading,
    failedToLoad: !isLoading && data === undefined,
  };
};

export const useUpdateDashboardConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveDashboardConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: RMS_BOOTSTRAP_QUERY_KEY,
      });
    },
  });
};
